import { emptyCardStackExtracted } from "@shared/lib/card-stack/extracted"
import { emptyProseMirrorContent } from "@shared/lib/card-stack/prose-mirror-utils"
import { isUnifiedCardStack } from "@shared/lib/card-stack/unified-migration"
import type { FindFn } from "@shared/models/analytics/base"
import type { CardStack } from "@shared/models/card-stack"
import type { PatchModel } from "@shared/models/patch"
import Patch from "@shared/models/patch-updater"
import { format, parseISO } from "date-fns"
import _ from "lodash"
import { cloneDeep, partial } from "lodash-es"
import type { PreparedAction } from "@shared/actions/action-doc-builder"
import type { Narrative } from "@shared/actions/create-narrative"
import defaultPlanbookLessonsActivity from "./default-planbook-lessons-activity"
import defaultPlanbookLessonsContext from "./default-planbook-lessons-context"

interface Payload {
  lessons: Array<CardStack>
  dateString: string
  planbookId: string
}

export function prepareClearAllLessonsForDay(payload: Payload): PreparedAction {
  return {
    name: PLANBOOK_CLEAR_ALL_LESSONS_FOR_DAY.name,
    payload: payload,
    patches: PLANBOOK_CLEAR_ALL_LESSONS_FOR_DAY.patches(payload),
    undoPatches: PLANBOOK_CLEAR_ALL_LESSONS_FOR_DAY.undoPatches(payload),
    narrative: partial(PLANBOOK_CLEAR_ALL_LESSONS_FOR_DAY.narrative, payload),
  }
}

export const PLANBOOK_CLEAR_ALL_LESSONS_FOR_DAY = {
  name: "PLANBOOK_CLEAR_ALL_LESSONS_FOR_DAY",

  patches(payload: Payload): Array<PatchModel> {
    return _.map(payload.lessons, (lesson) => {
      let patch = Patch.create("card-stack", lesson.id)
        .set("attributes.title", "")
        .set("attributes.cards", [])
      if (isUnifiedCardStack(lesson)) {
        patch
          .set("attributes.proseMirror", emptyProseMirrorContent())
          .set("attributes.extracted", emptyCardStackExtracted())
      }
      return patch.patch
    })
  },

  undoPatches(payload: Payload): Array<PatchModel> {
    return _.flatMap(payload.lessons, (lesson) => {
      let patch = Patch.create("card-stack", lesson.id)
        .set("attributes.title", lesson.attributes.title)
        .set("attributes.cards", cloneDeep(lesson.attributes.cards))
        .inc("attributes.version", 1)
      if (isUnifiedCardStack(lesson)) {
        patch
          .set("attributes.proseMirror", cloneDeep(lesson.attributes.proseMirror))
          .set("attributes.extracted", cloneDeep(lesson.attributes.extracted))
      }
      return patch.patch
    })
  },

  narrative(payload: Payload, findFn: FindFn, userId: string): Narrative | null {
    let planbook = findFn("planbook", payload.planbookId)
    // let models = defaultLessonModels(payload, findFn, userId)
    let formattedDate = format(parseISO(payload.dateString), "EEE, MMM. d, yyyy")
    // let newEvent: LessonDelete = {
    //   ...createBlankEvent(),
    //   planbook_id: models.planbook.id,
    //   primary_object_id: payload.cardStackId,
    //   date_plan_scheduled_for: models.dateStringForCardStack,
    //   primary_object_id_and_ancestor_ids: models.cardStackIdAndAncestorIds,
    //   primary_verb: "delete",
    //   primary_possessive: getPrimaryPossiveForPlanbook(findFn, userId, models.planbook.id),
    //   primary_object: "lesson",
    //   primary_object_specifier: null,
    //   adverbial_verb: "removing",
    //   adverbial_object: "lesson",
    //   adverbial_object_specifier: null,
    //   adverbial_object_id: null,
    //   adverbial_object_id_and_ancestor_ids: null,
    // }
    return {
      title: "Cleared An Entire Day of Lessons",
      titleForAnalytics: "Lesson - Clear Entire Day",
      description: `Cleared all the lessons on ${formattedDate}. Did not erase their lesson histories`,
      context: defaultPlanbookLessonsContext(planbook, payload),
      activity: defaultPlanbookLessonsActivity(payload.planbookId, userId),
      // newAnalyticsEvents: [newEvent],
    }
  },
}

export default PLANBOOK_CLEAR_ALL_LESSONS_FOR_DAY
